import React from "react";
import { useTranslation } from "react-i18next";
import { Check, Sparkles } from "lucide-react";

// Tier metadata — prices stay the same; copy comes from i18n
const TIER_META = [
  { id: "basic", price: "€0", perDay: null, periodKey: "alwaysFree", highlight: false },
  { id: "premium", price: "€4,99", perDay: "€0,16", periodKey: "perMonth", highlight: false },
  { id: "pro", price: "€14,99", perDay: "€0,50", periodKey: "perMonth", highlight: true, badge: true },
  { id: "professional", price: "€24,99", perDay: "€0,83", periodKey: "perMonth", highlight: false },
];

export const Pricing = () => {
  const { t } = useTranslation();

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="bg-background py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
              {t("pricing.eyebrow")}
            </p>
            <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] text-[hsl(var(--ink))] text-balance sm:text-5xl lg:text-6xl">
              {t("pricing.title")}
            </h2>
            <p className="mt-6 text-[hsl(var(--ink-soft))]">
              {t("pricing.introBefore")}{" "}
              <span className="font-medium text-[hsl(var(--ink))]">{t("pricing.introBold")}</span>{" "}
              {t("pricing.introAfter")}
            </p>
          </div>

          <div
            data-testid="pricing-trial-banner"
            className="inline-flex items-center gap-3 self-start rounded-full border border-[hsl(var(--sand))] bg-[hsl(var(--sand))]/10 px-5 py-3 text-sm text-[hsl(var(--ink))]"
          >
            <Sparkles className="h-4 w-4 text-[hsl(var(--sand-dark))]" strokeWidth={1.6} />
            <span>
              <span className="font-medium">{t("pricing.bannerBold")}</span>{" "}
              {t("pricing.banner").replace(t("pricing.bannerBold"), "").trim().replace(/^[?:]\s*/, "")}
            </span>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          {TIER_META.map((tier) => {
            const features = t(`pricing.tiers.${tier.id}.features`, { returnObjects: true }) || [];
            return (
              <article
                key={tier.id}
                data-testid={`pricing-${tier.id}`}
                className={`relative flex flex-col rounded-3xl border p-7 transition-all hover:-translate-y-1 ${
                  tier.highlight
                    ? "border-[hsl(var(--forest))] bg-[hsl(var(--forest))] text-[hsl(var(--background))] xl:scale-[1.04]"
                    : "border-[hsl(var(--border))] bg-[hsl(var(--surface))]"
                }`}
              >
                {tier.badge && (
                  <span
                    className={`absolute -top-3 left-7 rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.22em] ${
                      tier.highlight
                        ? "bg-[hsl(var(--sand))] text-[hsl(var(--ink))]"
                        : "border border-[hsl(var(--sand-dark))] bg-[hsl(var(--surface))] text-[hsl(var(--sand-dark))]"
                    }`}
                  >
                    {t("pricing.badgeMostChosen")}
                  </span>
                )}

                <p
                  className={`text-xs uppercase tracking-[0.22em] ${
                    tier.highlight
                      ? "text-[hsl(var(--background))]/65"
                      : "text-[hsl(var(--ink-soft))]"
                  }`}
                >
                  {t(`pricing.tiers.${tier.id}.name`)}
                </p>

                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-semibold tracking-tight">
                    {tier.price}
                  </span>
                  <span
                    className={`text-sm ${
                      tier.highlight
                        ? "text-[hsl(var(--background))]/70"
                        : "text-[hsl(var(--ink-soft))]"
                    }`}
                  >
                    {t(`pricing.${tier.periodKey}`)}
                  </span>
                </div>
                {tier.perDay && (
                  <p
                    className={`mt-2 text-xs uppercase tracking-[0.18em] ${
                      tier.highlight
                        ? "text-[hsl(var(--sand))]"
                        : "text-[hsl(var(--sand-dark))]"
                    }`}
                  >
                    · {tier.perDay} {t("pricing.perDay")}
                  </p>
                )}
                <p
                  className={`mt-4 text-sm ${
                    tier.highlight
                      ? "text-[hsl(var(--background))]/75"
                      : "text-[hsl(var(--ink-soft))]"
                  }`}
                >
                  {t(`pricing.tiers.${tier.id}.description`)}
                </p>

                <ul className="mt-7 flex-1 space-y-3 text-sm">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check
                        className={`mt-0.5 h-4 w-4 shrink-0 ${
                          tier.highlight
                            ? "text-[hsl(var(--sand))]"
                            : "text-[hsl(var(--forest))]"
                        }`}
                        strokeWidth={2}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#waitlist"
                  data-testid={`pricing-cta-${tier.id}`}
                  className={`mt-8 inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-medium transition-all ${
                    tier.highlight
                      ? "bg-[hsl(var(--sand))] text-[hsl(var(--ink))] hover:bg-[hsl(var(--sand-dark))]"
                      : "border border-[hsl(var(--ink))] text-[hsl(var(--ink))] hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--background))]"
                  }`}
                >
                  {t(`pricing.tiers.${tier.id}.cta`)}
                </a>
              </article>
            );
          })}
        </div>

        <p className="mt-8 text-center text-xs text-[hsl(var(--ink-soft))]">
          {t("pricing.footnote")}
        </p>
      </div>
    </section>
  );
};

export default Pricing;
